import { Injectable } from '@angular/core';
import {
	ChartCardConfig,
	ChartDataConfig,
	Granularity,
} from '../models/chart-config.model';
import {
	chartHasTimeDimension,
	getPrimaryChartTimeDimension,
	timeDimensionConfigFromPrimary,
} from '../utils/chart-card-date-range.util';
import {
	DataMigrationResult,
	DataMigrationOptions,
	DEFAULT_MIGRATION_OPTIONS,
	ValidationWarning,
	WarningSeverity,
	CHART_TYPE_REQUIREMENTS,
	ChartTypeRequirements,
	chartTypeLabel,
} from './chart-config-change.types';
import {
	buildDefaultChartDataOrder,
	orderConfigForPersistence,
} from '../components/editors/shared/data-tab/chart-data-order.util';

/**
 * Migrates data configuration when switching between chart types.
 * Switch-back uses server revision history (undo), not session caches.
 */
@Injectable({
	providedIn: 'root',
})
export class ChartDataMigrationService {
	/**
	 * Migrates data from one chart type to another.
	 *
	 * @param currentData - Current data configuration
	 * @param fromChartType - Source chart type
	 * @param toChartType - Target chart type
	 * @param options - Migration options
	 * @returns Migration result with migrated data and warnings
	 */
	migrateData(
		currentData: ChartDataConfig | undefined,
		fromChartType: string,
		toChartType: string,
		options: DataMigrationOptions = DEFAULT_MIGRATION_OPTIONS
	): DataMigrationResult {
		// No data to migrate
		if (!currentData) {
			return {
				success: true,
				data: undefined,
				warnings: [],
			};
		}

		const warnings: ValidationWarning[] = [];
		const fromReqs = CHART_TYPE_REQUIREMENTS[fromChartType];
		const toReqs = CHART_TYPE_REQUIREMENTS[toChartType];

		// If either type is unknown, keep data as-is with warning
		if (!fromReqs || !toReqs) {
			warnings.push({
				code: 'UNKNOWN_CHART_TYPE_MIGRATION',
				message: `Migrating from ${fromChartType} to ${toChartType}: requirements unknown, keeping data unchanged`,
				severity: WarningSeverity.Info,
			});
			return {
				success: true,
				data: currentData,
				warnings,
			};
		}

		// Start with current data as base
		const migratedData: ChartDataConfig = { ...currentData };

		// Handle metrics migration
		if (currentData.metrics && currentData.metrics.length > 0) {
			const migratedMetrics = this.migrateMetrics(
				currentData.metrics,
				fromReqs,
				toReqs,
				options.metricsStrategy,
				warnings
			);
			migratedData.metrics = migratedMetrics;
		}

		const timeMigration = this.migrateTimeDimension(
			currentData,
			toChartType,
			fromReqs,
			toReqs,
			warnings
		);
		migratedData.timeDimension = timeMigration.timeDimension;
		const dataRecord = migratedData as Record<string, unknown>;
		if (timeMigration.clearTimeDimensions) {
			delete dataRecord['timeDimensions'];
		}

		// Handle dimensions migration
		if (currentData.dimensions) {
			migratedData.dimensions = this.migrateDimensions(
				currentData.dimensions,
				fromReqs,
				toReqs,
				options.dimensionsStrategy,
				warnings
			);
		}

		// Handle filters migration (preserve if possible)
		if (options.preserveFilters && currentData.filters) {
			migratedData.filters = currentData.filters;
		}

		if (fromChartType !== toChartType) {
			const order = orderConfigForPersistence(
				buildDefaultChartDataOrder(toChartType, migratedData)
			);
			if (order) {
				migratedData.order = order;
			} else {
				delete migratedData.order;
			}
		}

		return {
			success: true,
			data: migratedData,
			warnings,
		};
	}

	/**
	 * Migrates metrics based on strategy.
	 */
	private migrateMetrics(
		metrics: ChartDataConfig['metrics'],
		fromReqs: ChartTypeRequirements,
		toReqs: ChartTypeRequirements,
		strategy: DataMigrationOptions['metricsStrategy'],
		warnings: ValidationWarning[]
	): ChartDataConfig['metrics'] {
		const metricCount = metrics?.length ?? 0;

		// If under limit, keep all
		if (!toReqs.maxMetrics || metricCount <= toReqs.maxMetrics) {
			return metrics;
		}

		// Over limit - apply strategy
		switch (strategy) {
			case 'truncate':
				warnings.push({
					code: 'METRICS_TRUNCATED',
					message: `Only the first ${toReqs.maxMetrics} metric${toReqs.maxMetrics === 1 ? '' : 's'} will be used.`,
					severity: WarningSeverity.Info,
					field: 'data.metrics',
				});
				return metrics?.slice(0, toReqs.maxMetrics);

			case 'keep':
				warnings.push({
					code: 'METRICS_EXCEED_LIMIT',
					message: `This chart type supports ${toReqs.maxMetrics} metric${toReqs.maxMetrics === 1 ? '' : 's'}; ${metricCount} are configured. Some may not display.`,
					severity: WarningSeverity.Warning,
					field: 'data.metrics',
				});
				return metrics;

			case 'error':
				warnings.push({
					code: 'METRICS_LIMIT_EXCEEDED',
					message: `Cannot switch chart type: at most ${toReqs.maxMetrics} metric${toReqs.maxMetrics === 1 ? '' : 's'} allowed (${metricCount} configured).`,
					severity: WarningSeverity.Blocking,
					field: 'data.metrics',
				});
				return metrics;
		}
	}

	/**
	 * Migrates time dimension based on requirements.
	 */
	private migrateTimeDimension(
		currentData: ChartDataConfig,
		toChartType: string,
		fromReqs: ChartTypeRequirements,
		toReqs: ChartTypeRequirements,
		warnings: ValidationWarning[]
	): {
		timeDimension: ChartDataConfig['timeDimension'];
		clearTimeDimensions: boolean;
	} {
		const cfg = {
			data: currentData,
		} as ChartCardConfig;
		const primary = getPrimaryChartTimeDimension(cfg);
		const hasTime = chartHasTimeDimension(cfg);
		const toLabel = chartTypeLabel(toChartType);

		// Switching TO time-series: normalize primary onto timeDimension when present
		if (toReqs.timeSeries) {
			if (hasTime && primary) {
				const timeDimension = timeDimensionConfigFromPrimary(primary);
				// Line and area plot a date axis; Day of week has no dates to plot.
				if (timeDimension.granularity === Granularity.DayOfWeek) {
					timeDimension.granularity = Granularity.Day;
					warnings.push({
						code: 'GRANULARITY_RESET',
						message: `Switched to ${toLabel}. Granularity changed from Day of week to Day.`,
						severity: WarningSeverity.Info,
						field: 'data.timeDimension.granularity',
					});
				}
				return {
					timeDimension,
					clearTimeDimensions: true,
				};
			}
			warnings.push({
				code: 'NEEDS_TIME_DIMENSION',
				message: `Switched to ${toLabel}. Set the date range in the Data tab.`,
				severity: WarningSeverity.Info,
				field: 'data.timeDimension',
				suggestedAction: 'Open the Data tab',
			});
			return { timeDimension: undefined, clearTimeDimensions: true };
		}

		// Leaving time-series: keep timeDimension for query window (date range / granularity).
		// Only the chart axis moves to dimensions[]; do not drop the fetch scope.
		if (!toReqs.timeSeries && fromReqs.timeSeries && hasTime) {
			const scope =
				primary !== undefined
					? timeDimensionConfigFromPrimary(primary)
					: currentData.timeDimension;
			return {
				timeDimension: scope,
				clearTimeDimensions: true,
			};
		}

		return {
			timeDimension: currentData.timeDimension,
			clearTimeDimensions: false,
		};
	}

	/**
	 * Migrates dimensions based on strategy.
	 */
	private migrateDimensions(
		dimensions: ChartDataConfig['dimensions'],
		fromReqs: ChartTypeRequirements,
		toReqs: ChartTypeRequirements,
		strategy: DataMigrationOptions['dimensionsStrategy'],
		warnings: ValidationWarning[]
	): ChartDataConfig['dimensions'] {
		const hasDimensions = dimensions && dimensions.length > 0;

		// Switching to category-based: keep dimensions
		if (toReqs.usesCategory) {
			if (!hasDimensions) {
				warnings.push({
					code: 'NEEDS_DIMENSION',
					message: 'Add a category dimension in the Data tab.',
					severity: WarningSeverity.Info,
					field: 'data.dimensions',
				});
			}
			return dimensions;
		}

		// Category → time-series: keep dimensions as optional series breakdown
		if (fromReqs.usesCategory && toReqs.timeSeries && hasDimensions) {
			return dimensions;
		}

		// Switching from category-based to a chart that does not use dimensions: clear
		if (
			fromReqs.usesCategory &&
			!toReqs.supportsDimensions &&
			hasDimensions
		) {
			switch (strategy) {
				case 'clear':
					warnings.push({
						code: 'CLEARED_DIMENSIONS',
						message: 'Category dimensions cleared for this chart type.',
						severity: WarningSeverity.Silent,
						field: 'data.dimensions',
					});
					return undefined;

				case 'keep':
					warnings.push({
						code: 'UNUSED_DIMENSIONS',
						message:
							'Category dimensions were kept but may not be used by this chart type.',
						severity: WarningSeverity.Info,
						field: 'data.dimensions',
					});
					return dimensions;

				case 'error':
					warnings.push({
						code: 'UNEXPECTED_DIMENSIONS',
						message: 'Chart type does not support dimensions',
						severity: WarningSeverity.Blocking,
						field: 'data.dimensions',
					});
					return dimensions;
			}
		}

		return dimensions;
	}
}
